import React from 'react';
import { Activity, BarChart3, TrendingUp, Target } from 'lucide-react';

const LeaguePerformanceBanner = () => {
  return (
    <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-xl overflow-hidden">
      {/* Enhanced Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-1/4 w-24 h-24 bg-blue-400/10 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-purple-400/10 rounded-full blur-md animate-pulse delay-300"></div>
        
        {/* Additional Floating Particles */}
        <div className="absolute top-10 right-10 w-6 h-6 bg-blue-400/30 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-10 left-20 w-4 h-4 bg-indigo-400/40 rounded-full animate-ping"></div>
        <div className="absolute top-1/2 left-10 w-8 h-8 bg-cyan-400/20 rounded-full animate-pulse delay-700"></div>
        <div className="absolute bottom-1/3 right-1/2 w-5 h-5 bg-blue-400/35 rounded-full animate-bounce delay-1000"></div>
        
        {/* Performance-themed floating elements */}
        <div className="absolute top-16 right-1/4 w-10 h-10 bg-cyan-400/20 rounded-lg animate-bounce delay-200 rotate-12"></div>
        <div className="absolute bottom-16 left-1/3 w-6 h-6 bg-blue-400/30 rounded-full animate-pulse delay-600"></div>
        <div className="absolute top-2/3 right-20 w-8 h-8 bg-indigo-400/25 rounded-lg animate-bounce delay-800 -rotate-12"></div>
      </div>

      {/* Subtle Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"
             style={{
               backgroundImage: `radial-gradient(circle at 20px 20px, rgba(255,255,255,0.1) 1px, transparent 1px)`,
               backgroundSize: '40px 40px'
             }}>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 p-8">
        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-4 gap-8 items-center min-h-[200px]">
          {/* Left Side - Beast Grade Image */}
          <div className="col-span-1 flex justify-center items-center h-full">
            <div className="relative">
              <img 
                src="/beastgrade.png" 
                alt="Beast Grade" 
                className="w-56 h-56 lg:w-72 lg:h-72 object-contain opacity-90 drop-shadow-2xl"
                style={{ 
                  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))',
                  transform: 'translateY(-8px)',
                  maskImage: 'radial-gradient(ellipse at center, black 20%, rgba(0,0,0,0.7) 35%, rgba(0,0,0,0.3) 55%, transparent 75%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, rgba(0,0,0,0.7) 35%, rgba(0,0,0,0.3) 55%, transparent 75%)'
                }}
              />
              {/* Glowing effect behind image */}
              <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full scale-75 -z-10"></div>
            </div>
          </div>

          {/* Center - Text Content */}
          <div className="col-span-2 text-center">
            <div className="flex items-center justify-center mb-6">
              <div>
                <h1 className="text-4xl lg:text-5xl font-black text-white mb-3 tracking-tight">
                  League Performance
                </h1>
                <p className="text-blue-100 text-lg lg:text-xl font-medium leading-relaxed">
                  Comprehensive league-wide scoring analysis, weekly performance trends, and competitive insights across all seasons
                </p>
                
                {/* Feature highlights */}
                <div className="flex items-center justify-center space-x-6 mt-6">
                  <div className="flex items-center space-x-2 text-blue-100">
                    <BarChart3 className="h-5 w-5 text-cyan-300" />
                    <span className="text-sm font-medium">Weekly Scoring</span>
                  </div>
                  <div className="flex items-center space-x-2 text-blue-100">
                    <TrendingUp className="h-5 w-5 text-cyan-300" />
                    <span className="text-sm font-medium">Performance Analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Empty space to match Legacy Score layout */}
          <div className="col-span-1"></div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden relative min-h-[200px] flex items-center justify-center">
          {/* Background Beast Grade Image */}
          <div className="absolute inset-0 flex items-center justify-center opacity-30">
            <img 
              src="/beastgrade.png" 
              alt="Beast Grade" 
              className="w-88 h-88 sm:w-104 sm:h-104 object-contain"
              style={{ 
                filter: 'blur(1px)',
                maskImage: 'radial-gradient(ellipse at center, black 25%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0.3) 65%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(ellipse at center, black 25%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0.3) 65%, transparent 80%)'
              }}
            />
          </div>
          
          {/* Mobile Text Content */}
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-3 tracking-tight">
              League Performance
            </h1>
            <p className="text-blue-100 text-base sm:text-lg font-medium leading-relaxed max-w-md mx-auto">
              Comprehensive league-wide scoring analysis, weekly performance trends, and competitive insights across all seasons
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaguePerformanceBanner;